import { ArrowThickRightIcon } from "@talladega/icons";
import {
  cleanParagraphTags,
  GetBackgroundStyle,
  IsNodeStructure,
} from "@talladega/helpers";
import classNames from "classnames";
import React, { useState } from "react";
import { ActionButton } from "@talladega/types";
import { CmsHeading } from "../CmsHeading";
import { MultiStepLayout } from "./MultiStepLayout";

export const MultiStepSection = ({
  sectionHeading,
  sectionSubheading,
  stepLayout,
  button,
  background,
  htmlIdRef,
  cmsButton,
  cmsIconImage,
  cmsButtonModalContainer,
  baseDomain,
}: MultiStepLayout) => {
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  return (
    <div
      id="cms-multi-step-section"
      className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="mx-auto max-w-page px-4 pb-6 pt-10 lg:px-5 lg:py-16">
        <article>
          {sectionHeading && (
            <CmsHeading
              headingLevel={sectionHeading.headingGroup?.headingLevel}
              textSize={sectionHeading.headingGroup?.textSize}
              text={sectionHeading.headingGroup?.text}
              htmlId={sectionHeading.headingGroup?.htmlId}
            />
          )}
          {sectionSubheading && (
            <div className="mt-4 lg:mt-8">
              <CmsHeading
                headingLevel={sectionSubheading.headingGroup?.headingLevel}
                textSize={sectionSubheading.headingGroup?.textSize}
                text={sectionSubheading.headingGroup?.text}
                htmlId={sectionSubheading.headingGroup?.htmlId}
              />
            </div>
          )}
        </article>
        <div className="flex flex-col flex-nowrap content-evenly pb-4 pt-12 text-center xl:flex-row xl:gap-5">
          {stepLayout.map((step, index) => {
            const { altText, sourceUrl } =
              (IsNodeStructure(step.stepIcon)
                ? step.stepIcon.node
                : step.stepIcon) ?? {};

            return (
              <React.Fragment key={step.stepLabel}>
                <div
                  key={step.stepLabel}
                  className={classNames(
                    "group mx-auto flex h-32 w-full min-w-0 flex-1 flex-row items-center rounded-lg transition md:w-80 lg:h-auto xl:flex-col",
                    {
                      "xl:pt-20": index === 0 || index === 2 || index === 4,
                      "xl:pt-6": index === 1,
                      "xl:pt-[7.5rem]": index === 3,
                    }
                  )}
                >
                  <div
                    key={step.stepSubLabel}
                    className="flex text-gray-500 transition duration-300 ease-in-out"
                  >
                    {sourceUrl && (
                      <>
                        <div
                          key={`${step.stepLabel}-image`}
                          className="relative mt-2 h-[4.47rem] w-[4.5rem] xl:h-[8.78rem] xl:w-[8.78rem]"
                        >
                          {cmsIconImage(index, {
                            src: sourceUrl,
                            alt: altText,
                          })}
                        </div>
                        {stepLayout.length === 5 && (
                          <div
                            key={`${step.stepLabel}-rightIcon-xl-5`}
                            className={classNames(
                              "absolute hidden text-gray-100",
                              {
                                "-rotate-[21deg] pl-[10rem] pt-10 xl:block":
                                  index === 0,
                                "rotate-[21deg] pl-[11rem] pt-10 xl:block":
                                  index === 1,
                                "rotate-[19deg] pl-[11rem] pt-14 xl:block":
                                  index === 2,
                                "-rotate-[19deg] pl-[10rem] pt-14 xl:block":
                                  index === 3,
                              }
                            )}
                          >
                            <ArrowThickRightIcon width={52} height={52} />
                          </div>
                        )}
                        {stepLayout.length === 4 && (
                          <div
                            key={`${step.stepLabel}-rightIcon-xl-4`}
                            className={classNames(
                              "absolute hidden text-gray-100",
                              {
                                "-rotate-[21deg] pl-[12rem] pt-7 xl:block":
                                  index === 0,
                                "rotate-[21deg] pl-[12rem] pt-14 xl:block":
                                  index === 1,
                                "rotate-[13deg] pl-[12rem] pt-16 xl:block":
                                  index === 2,
                              }
                            )}
                          >
                            <ArrowThickRightIcon width={65} height={65} />
                          </div>
                        )}
                        {stepLayout.length === 3 && (
                          <div
                            key={`${step.stepLabel}-rightIcon-xl-3`}
                            className={classNames(
                              "absolute hidden text-gray-100",
                              {
                                "-rotate-[21deg] pl-[14rem] pt-7 xl:block":
                                  index === 0,
                                "rotate-[21deg] pl-[15rem] pt-8 xl:block":
                                  index === 1,
                              }
                            )}
                          >
                            <ArrowThickRightIcon width={85} height={85} />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div
                    key={step.stepLabel + step.stepSubLabel}
                    className="flex flex-col justify-center px-3 text-left xl:hidden"
                  >
                    <p
                      className="text-lg font-semibold"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: cleanParagraphTags(step.stepLabel),
                      }}
                    />
                    <p
                      className="text-md pt-2 font-normal"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: cleanParagraphTags(step.stepSubLabel),
                      }}
                    />
                  </div>

                  <h5
                    className="hidden pt-8 transition duration-300 ease-in-out xl:block"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: cleanParagraphTags(step.stepLabel),
                    }}
                  />
                  <p
                    className="text-md hidden pb-8 pt-3 font-normal xl:block"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: cleanParagraphTags(step.stepSubLabel),
                    }}
                  />
                </div>
                <div
                  key={`${step.stepLabel}-rightIcon`}
                  className="mx-auto my-3 rotate-90 text-gray-100 last:hidden xl:hidden"
                >
                  <ArrowThickRightIcon width={50} height={25} />
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="flex justify-center">
          {!button.isHidden && (
            <div className="w-56 pt-8 xl:pt-0">
              {cmsButton(setButtonModalState, button, htmlIdRef)}
            </div>
          )}
        </div>
      </div>
      {buttonModalState.show && cmsButtonModalContainer(buttonModalState)}
    </div>
  );
};

export default {};
